import React, { useEffect, useRef, useState } from 'react';
import { Camera, CameraOff, Mic, MicOff, PhoneOff, Sparkles, User, Loader2, Maximize2, Volume2, VolumeX } from 'lucide-react';
import { supabase } from '../services/geminiService';

interface LiveVideoRoomProps {
  memberEmail: string;
  isAdmin: boolean;
  onClose: () => void;
  audioOnly?: boolean;
}

const LiveVideoRoom: React.FC<LiveVideoRoomProps> = ({ memberEmail, isAdmin, onClose, audioOnly = false }) => {
  const [localStream, setLocalStream] = useState<MediaStream | null>(null);
  const [remoteStream, setRemoteStream] = useState<MediaStream | null>(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(audioOnly);
  const [connectionStatus, setConnectionStatus] = useState<'connecting' | 'connected' | 'ended'>('connecting');

  const localVideoRef = useRef<HTMLVideoElement>(null);
  const remoteVideoRef = useRef<HTMLVideoElement>(null);
  const pc = useRef<RTCPeerConnection | null>(null);
  const channelRef = useRef<any>(null);

  const configuration: RTCConfiguration = {
    iceServers: [{ urls: 'stun:stun.l.google.com:19302' }]
  };

  useEffect(() => {
    const init = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ 
          video: !audioOnly, 
          audio: true 
        });
        setLocalStream(stream);
        if (localVideoRef.current) localVideoRef.current.srcObject = stream;

        // Setup Signaling Channel
        const channelId = `live-ritual-${memberEmail.replace(/[^a-zA-Z0-9]/g, '-')}`;
        channelRef.current = supabase.channel(channelId);

        channelRef.current
          .on('broadcast', { event: 'signal' }, async ({ payload }: any) => {
            if (!pc.current) return;

            if (payload.type === 'offer' && !isAdmin) {
              await pc.current.setRemoteDescription(new RTCSessionDescription(payload));
              const answer = await pc.current.createAnswer();
              await pc.current.setLocalDescription(answer);
              channelRef.current.send({
                type: 'broadcast',
                event: 'signal',
                payload: answer
              });
            } else if (payload.type === 'answer' && isAdmin) {
              await pc.current.setRemoteDescription(new RTCSessionDescription(payload));
            } else if (payload.type === 'candidate') {
              try {
                await pc.current.addIceCandidate(new RTCIceCandidate(payload.candidate));
              } catch (e) {
                console.error('Error adding ice candidate', e);
              }
            } else if (payload.type === 'end') {
              onClose();
            }
          })
          .subscribe();

        // Create Peer Connection
        pc.current = new RTCPeerConnection(configuration);

        stream.getTracks().forEach(track => {
          pc.current?.addTrack(track, stream);
        });

        pc.current.ontrack = (event) => {
          setRemoteStream(event.streams[0]);
          if (remoteVideoRef.current) remoteVideoRef.current.srcObject = event.streams[0];
          setConnectionStatus('connected');
        };

        pc.current.onicecandidate = (event) => {
          if (event.candidate) {
            channelRef.current.send({
              type: 'broadcast',
              event: 'signal',
              payload: { type: 'candidate', candidate: event.candidate }
            });
          }
        };

        if (isAdmin) {
          const offer = await pc.current.createOffer();
          await pc.current.setLocalDescription(offer);
          channelRef.current.send({
            type: 'broadcast',
            event: 'signal',
            payload: offer
          });
        }
      } catch (err) {
        console.error('Failed to get media', err);
        alert('Could not access celestial frequencies (Camera/Mic).');
        onClose();
      }
    };

    init();

    return () => {
      localStream?.getTracks().forEach(track => track.stop());
      pc.current?.close();
      if (channelRef.current) {
        channelRef.current.send({ type: 'broadcast', event: 'signal', payload: { type: 'end' } });
        supabase.removeChannel(channelRef.current);
      }
    };
  }, []);

  const toggleMute = () => {
    if (localStream) {
      localStream.getAudioTracks()[0].enabled = isMuted;
      setIsMuted(!isMuted);
    }
  };

  const toggleVideo = () => {
    if (audioOnly) return;
    if (localStream) {
      localStream.getVideoTracks()[0].enabled = isVideoOff;
      setIsVideoOff(!isVideoOff);
    }
  };

  return (
    <div className="fixed inset-0 z-[2000] bg-slate-950 flex flex-col animate-fade-in overflow-hidden">
      {/* Cinematic Header Overlay */}
      <div className="absolute top-0 left-0 right-0 p-8 z-30 flex justify-between items-center pointer-events-none">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-amber-500 text-slate-950 rounded-sm shadow-xl animate-pulse pointer-events-auto">
            <Sparkles size={24} />
          </div>
          <div className="text-left">
            <h2 className="text-2xl font-serif text-white tracking-widest uppercase">{audioOnly ? 'Audio' : 'Video'} Sanctuary</h2>
            <p className="text-[10px] text-amber-500 uppercase tracking-[0.3em] font-bold">Divine Channel Open</p>
          </div>
        </div>
        
        {connectionStatus === 'connecting' && (
          <div className="flex items-center gap-3 bg-indigo-950/40 backdrop-blur-md px-4 py-2 border border-indigo-500/30 rounded-full text-indigo-300 text-[10px] uppercase tracking-widest font-bold">
            <Loader2 size={14} className="animate-spin" /> Aligning Frequencies...
          </div>
        )}
      </div>

      {/* Main Stream Area */}
      <div className="flex-1 relative bg-[#02040a] flex items-center justify-center">
        {audioOnly ? (
          <div className="flex flex-col items-center gap-8 animate-pulse">
            <div className="w-48 h-48 rounded-full border-4 border-amber-500/20 flex items-center justify-center bg-indigo-950/20 relative">
              <div className="absolute inset-0 border-4 border-amber-500 border-t-transparent rounded-full animate-spin" />
              <Volume2 size={80} className="text-amber-500" />
            </div>
            <div className="text-center space-y-2">
              <h3 className="text-xl font-serif text-white uppercase tracking-widest">{isAdmin ? 'Consulting Seeker' : 'Listening to Allie'}</h3>
              <p className="text-[10px] text-indigo-400 uppercase tracking-widest font-bold">Direct Audio Channel</p>
            </div>
            <video ref={remoteVideoRef} autoPlay playsInline className="hidden" />
          </div>
        ) : (
          <div className="absolute inset-0">
            {remoteStream ? (
              <video
                ref={remoteVideoRef}
                autoPlay
                playsInline
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center space-y-4 opacity-20">
                <div className="w-24 h-24 border-2 border-dashed border-amber-500 rounded-full flex items-center justify-center animate-[spin_10s_linear_infinite]">
                   <Sparkles size={40} className="text-amber-500" />
                </div>
                <p className="text-sm uppercase tracking-[0.4em] text-indigo-400">Waiting for Resonance</p>
              </div>
            )}
          </div>
        )}

        {/* Local Video Overlay (only for Video calls) */}
        {!audioOnly && (
          <div className="absolute bottom-32 right-8 w-48 md:w-64 aspect-video bg-slate-900 border-2 border-amber-500/30 rounded-sm shadow-2xl overflow-hidden z-20">
            <video
              ref={localVideoRef}
              autoPlay
              playsInline
              muted
              className="w-full h-full object-cover"
            />
            <div className="absolute bottom-2 left-2 bg-slate-950/60 px-2 py-1 rounded text-[8px] uppercase tracking-widest text-white">
              Your Manifestation
            </div>
            {isVideoOff && (
              <div className="absolute inset-0 bg-slate-950 flex items-center justify-center">
                <CameraOff size={24} className="text-indigo-500" />
              </div>
            )}
          </div>
        )}

        {/* Subtle Gradient Overlays */}
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent to-slate-950/20 pointer-events-none" />
      </div>

      {/* Control Bar */}
      <div className="h-28 bg-slate-900/80 backdrop-blur-2xl border-t border-indigo-900/30 flex items-center justify-center gap-6 md:gap-12 relative z-30">
        <button
          onClick={toggleMute}
          className={`p-5 rounded-full border transition-all duration-300 ${isMuted ? 'bg-rose-600 border-rose-600 text-white' : 'bg-slate-800 border-indigo-900/50 text-indigo-300 hover:border-amber-500'}`}
        >
          {isMuted ? <MicOff size={24} /> : <Mic size={24} />}
        </button>

        {!audioOnly && (
          <button
            onClick={toggleVideo}
            className={`p-5 rounded-full border transition-all duration-300 ${isVideoOff ? 'bg-rose-600 border-rose-600 text-white' : 'bg-slate-800 border-indigo-900/50 text-indigo-300 hover:border-amber-500'}`}
          >
            {isVideoOff ? <CameraOff size={24} /> : <Camera size={24} />}
          </button>
        )}

        <button
          onClick={onClose}
          className="p-6 bg-rose-600 text-white rounded-full shadow-[0_0_30px_rgba(225,29,72,0.4)] hover:scale-110 active:scale-95 transition-all"
        >
          <PhoneOff size={32} />
        </button>

        <button
          className="p-5 rounded-full bg-slate-800 border border-indigo-900/50 text-indigo-300 hover:text-amber-500 transition-all"
          title="Full Manifestation"
        >
          <Maximize2 size={24} />
        </button>
      </div>
    </div>
  );
};

export default LiveVideoRoom;